import { useState } from 'react';
import { toIsoEndOfDay, toIsoStartOfDay } from '../lib/format';

export interface DateRange {
  from?: string;
  to?: string;
}

type PresetKey = 'today' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom';

const PRESETS: Array<{ key: PresetKey; label: string }> = [
  { key: 'today', label: 'Hoje' },
  { key: 'last7', label: 'Últimos 7 dias' },
  { key: 'last30', label: 'Últimos 30 dias' },
  { key: 'thisMonth', label: 'Este mês' },
  { key: 'lastMonth', label: 'Mês anterior' },
  { key: 'custom', label: 'Personalizado' },
];

function computeRange(preset: PresetKey, customFrom: string, customTo: string): DateRange {
  const now = new Date();

  switch (preset) {
    case 'today':
      return { from: toIsoStartOfDay(now), to: toIsoEndOfDay(now) };
    case 'last7': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { from: toIsoStartOfDay(start), to: toIsoEndOfDay(now) };
    }
    case 'last30': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { from: toIsoStartOfDay(start), to: toIsoEndOfDay(now) };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: toIsoStartOfDay(start), to: toIsoEndOfDay(now) };
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: toIsoStartOfDay(start), to: toIsoEndOfDay(end) };
    }
    case 'custom':
      return {
        from: customFrom ? toIsoStartOfDay(new Date(customFrom)) : undefined,
        to: customTo ? toIsoEndOfDay(new Date(customTo)) : undefined,
      };
  }
}

export function PeriodFilter({ onChange }: { onChange: (range: DateRange) => void }) {
  const [preset, setPreset] = useState<PresetKey>('last30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  function applyPreset(next: PresetKey) {
    setPreset(next);
    if (next !== 'custom') {
      onChange(computeRange(next, customFrom, customTo));
    }
  }

  function applyCustom(from: string, to: string) {
    setCustomFrom(from);
    setCustomTo(to);
    onChange(computeRange('custom', from, to));
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESETS.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => applyPreset(option.key)}
          className={`border px-3 py-1.5 font-display text-sm font-medium transition-colors ${
            preset === option.key
              ? 'border-brand-500 bg-brand-500 text-white'
              : 'border-[rgb(var(--border))] bg-transparent text-[rgb(var(--text-muted))] hover:border-brand-500 hover:text-[rgb(var(--text))]'
          }`}
        >
          {option.label}
        </button>
      ))}
      {preset === 'custom' ? (
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="input w-auto"
            value={customFrom}
            onChange={(event) => applyCustom(event.target.value, customTo)}
          />
          <span className="text-sm text-[rgb(var(--text-muted))]">até</span>
          <input type="date" className="input w-auto" value={customTo} onChange={(event) => applyCustom(customFrom, event.target.value)} />
        </div>
      ) : null}
    </div>
  );
}
